// ProgramPanel is where the user's robot program is displayed.

import { useGameStateContext } from 'app/src/game/GameStateContext';
import InstructionNode, {
	InstructionNodeDragData,
	InstructionNodeDragType,
	InstructionNodeDropResult,
} from 'app/src/game/InstructionNode';
import { useInstructionsArchiveContext } from 'app/src/game/InstructionsArchiveContext';
import { useProgramStoreContext } from 'app/src/game/ProgramStoreContext';
import useForceUpdate from 'app/src/utils/useForceUpdate';
import useValueChanged from 'app/src/utils/useValueChanged';
import { XYCoord } from 'dnd-core';
import * as React from 'react';
import { useDrop } from 'react-dnd';

const INSTRUCTION_NODE_WIDTH = 66;

export default function ProgramPanel(): React.ReactElement | null {
	const { gameStateStore } = useGameStateContext();
	const {
		program,
		addInstruction,
		removeInstruction,
		reorderInstruction,
	} = useProgramStoreContext();
	const { instructionCounts, setInstructionCount } = useInstructionsArchiveContext();

	const forceUpdate = useForceUpdate();

	const placeholderIndexRef = React.useRef<number | null>(null);
	const draggingIndexRef = React.useRef<number | null>(null);

	const containerRef = React.useRef<HTMLDivElement | null>(null);
	const instructionsRef = React.useRef<HTMLDivElement | null>(null);

	const [dropCount, setDropCount] = React.useState(0);

	const [, drop] = useDrop<InstructionNodeDragData, InstructionNodeDropResult, void>({
		accept: [InstructionNodeDragType],
		drop: ({ instructionType }) => {
			const oldIndex = draggingIndexRef.current;
			const newIndex = placeholderIndexRef.current;

			// Reset drag state and cause update.
			draggingIndexRef.current = null;
			placeholderIndexRef.current = null;
			setDropCount(dropCount + 1);

			if (newIndex !== null) {
				if (oldIndex === null) {
					// Add the instruction.
					addInstruction(instructionType, newIndex);

					// Deprement the count in the archive.
					const currentCount = instructionCounts.get(instructionType) || 0;
					setInstructionCount(instructionType, currentCount - 1);
				} else {
					// Move the instruction.
					reorderInstruction(oldIndex, newIndex);
				}
			}

			return {
				handled: true,
			};
		},
		hover: (_, monitor) => {
			if (!instructionsRef.current) {
				return;
			}

			if (monitor.isOver()) {
				// Determine instructions container bounds.
				const hoverBoundingRect = instructionsRef.current.getBoundingClientRect();

				// Determine mouse position.
				const clientOffset = monitor.getClientOffset();

				// Get X position relative to instructions container.
				const hoverClientX = (clientOffset as XYCoord).x - hoverBoundingRect.left;

				// Figure out the index of the slot currently being hovered.
				const hoverIndex = Math.min(
					Math.floor(hoverClientX / INSTRUCTION_NODE_WIDTH),
					program.length,
				);

				// If there's no placeholder or the placeholder is in a different slot,
				// move the placeholder to the hovered slot.
				if (placeholderIndexRef.current !== hoverIndex) {
					placeholderIndexRef.current = hoverIndex;
					forceUpdate();
				}
			} else {
				// The user dragged the tile off the program panel. Remove the
				// placeholder if there is one.
				if (placeholderIndexRef.current !== null) {
					placeholderIndexRef.current = null;
					forceUpdate();
				}
			}
		},
		collect: (monitor) => {
			if (!monitor.isOver() && typeof placeholderIndexRef.current === 'number') {
				placeholderIndexRef.current = null;
				forceUpdate();
			}
		},
	});

	drop(instructionsRef);

	const programChanged = useValueChanged(program);
	React.useEffect(() => {
		if (
			programChanged &&
			program.length - programChanged.previous.length === 1 &&
			containerRef.current
		) {
			containerRef.current.scrollTo(containerRef.current.scrollWidth, 0);
		}
	});

	const allowHighlights = !(
		gameStateStore.gameIsFinished &&
		(gameStateStore.actionResults.length === 0 ||
			gameStateStore.currentActionResultIndex === gameStateStore.actionResults.length)
	);

	React.useEffect(() => {
		if (
			programChanged &&
			program.length - programChanged.previous.length === 1 &&
			containerRef.current
		) {
			containerRef.current.scrollTo(containerRef.current.scrollWidth, 0);
		}
	});

	const minContainerSlots = program.length + (placeholderIndexRef.current === null ? 0 : 1);

	return (
		<div className="ProgramPanel" ref={containerRef}>
			<div
				className="ProgramPanel-innerContainer"
				ref={instructionsRef}
				key={`innerContainer--${dropCount}`}
				style={{ minWidth: minContainerSlots * INSTRUCTION_NODE_WIDTH + 4 }}
			>
				{program.map((instruction, index) => {
					const handleRemoveInstruction = (): void => {
						const currentCount = instructionCounts.get(instruction) || 0;

						setInstructionCount(instruction, currentCount + 1);
						removeInstruction(index);
					};

					const handleClick = (): void => {
						handleRemoveInstruction();
					};

					const handleDrop = (handled: boolean): void => {
						if (!handled) {
							draggingIndexRef.current = null;
							placeholderIndexRef.current = null;
							setDropCount(dropCount + 1);

							handleRemoveInstruction();
						}
					};

					const handleDrag = (): void => {
						draggingIndexRef.current = index;
						forceUpdate();
					};

					// Figure out where this node should land in the list. Dragging
					// instructions around or dragging new ones in from the archive can
					// cause them to shift.
					let adjustedIndex = index;

					if (draggingIndexRef.current !== null && draggingIndexRef.current < index) {
						adjustedIndex -= 1;
					}

					if (
						placeholderIndexRef.current !== null &&
						placeholderIndexRef.current <= adjustedIndex
					) {
						adjustedIndex += 1;
					}

					const left = adjustedIndex * INSTRUCTION_NODE_WIDTH;

					// If we start dragging a node from the panel, hide it. We use opacity
					// because react-dnd depends on still being able to measure the node's
					// position, and if we use display:none or visibility:hidden on the
					// parent, it can no longer get that position.
					const opacity = index === draggingIndexRef.current ? 0 : 1;

					return (
						<div
							className="ProgramPanel-instructionNodeContainer"
							key={`instructionNodeContainer--${index}`}
							style={{ left, opacity }}
						>
							<button className="ProgramPanel-button" onClick={handleClick}>
								<InstructionNode
									type={instruction}
									highlighted={allowHighlights && gameStateStore.currentTurn === index}
									onDrag={handleDrag}
									onDrop={handleDrop}
								/>
							</button>
						</div>
					);
				})}
			</div>
		</div>
	);
}
